import { AnalyticsParentPage } from '@chewy/loyalty-common/constants';
import { generateMarketPathname } from '@chewy/market-handler';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { SEGMENT_TAGS } from '@/analytics/tag-definitions';
import { CONFIRMATION_SHEET_QUERY_PARAM } from '@/components/navigation/constants';
import { useFeatureValue } from '@/hooks/useFeatureValue';
import { useAnalyticsContext } from '@/providers/AnalyticsProvider';
import { useMarket } from '@/providers/MarketProvider';
import { FeatureConfigKeys } from '@/utils/athena-feature-config';
import { routeToSignupNativeSheet } from '@/utils/router';

import { useDeviceSupportsNativeScrollingExperience } from './useDeviceSupportsNativeScrollingExperience';

const BASE_PATH = '/app/membership';

interface Props {
  page: AnalyticsParentPage;
  utilizeQueryParam?: boolean;
  showWebExperience?: boolean;
}

export const useSheetActions = ({
  utilizeQueryParam = false,
  showWebExperience = true,
  page,
}: Props) => {
  const router = useRouter();
  const { market } = useMarket();
  const { trackSegmentEvent } = useAnalyticsContext();
  const confirmationParam = router.query?.confirmation;
  const [showSheet, setShowSheet] = useState(false);

  const signupNativeSheetsEnabled = useFeatureValue(FeatureConfigKeys.SIGN_UP_NATIVE_SHEETS, false);
  const deviceSupportsNativeExperience = useDeviceSupportsNativeScrollingExperience();
  const showNativeSheetExperience =
    signupNativeSheetsEnabled && !showWebExperience && deviceSupportsNativeExperience;

  const handleOpenConfirmationSheet = useCallback(() => {
    if (showNativeSheetExperience) {
      routeToSignupNativeSheet(market);
    } else {
      setShowSheet(true);
      if (utilizeQueryParam) {
        const route = {
          pathname: router.pathname,
          query: { ...router.query, confirmation: 'true' },
        };
        const as = generateMarketPathname(
          `${BASE_PATH}${router.pathname}${CONFIRMATION_SHEET_QUERY_PARAM}`,
          market,
        );

        router.push(route, as, { shallow: true });
      }
    }
  }, [showNativeSheetExperience, market, utilizeQueryParam, router]);

  const handleOpenSheet = useCallback(() => {
    setShowSheet(true);
  }, []);

  const handleCloseSheet = useCallback(() => {
    if (utilizeQueryParam) {
      const { ...restQuery } = router.query;
      const route = {
        pathname: router.pathname,
        query: restQuery,
      };
      const as = generateMarketPathname(`${BASE_PATH}${router.pathname}`, market);
      router.push(route, as, { shallow: true });
    }

    setShowSheet(false);

    if (page !== AnalyticsParentPage.Landing) {
      trackSegmentEvent(
        SEGMENT_TAGS.ClosesSignupFlyout.eventName,
        SEGMENT_TAGS.ClosesSignupFlyout.data,
      );
    }
  }, [router, trackSegmentEvent, utilizeQueryParam, market, page]);

  useEffect(() => {
    if (utilizeQueryParam && confirmationParam === 'true') {
      if (showNativeSheetExperience) {
        routeToSignupNativeSheet(market);
        return;
      }
      setShowSheet(true);
    }
  }, [confirmationParam, utilizeQueryParam, showNativeSheetExperience, market]);

  return {
    showSheet,
    handleOpenConfirmationSheet,
    handleOpenSheet,
    handleCloseSheet,
  };
};
